import {
    Button,
    Icon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
} from '@ksyos/design-system';
import { useState } from 'react';
import type { HolterRecorderItem } from '@common/interfaces/cardiology/holterRecorderItem';
import {
    ActiveOrArchived,
    CustomControlStatus,
    DiaryTypes,
    EventEcgCardioExaminationDuration,
    HolterCardioExaminationDuration,
    RecorderType,
} from '@common/enums';
import { OpnameDuur } from '@common/enums/opnameDuur';
import { FullName } from '@frontend/components/elements/common';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { NullableStringBirthDate } from '@frontend/components/elements/NullableStringBirthDate';
import { HolterReadAndUploadDairyModel } from '@frontend/components/pages/holters/holterReadAndUploadDairyModel';
import { getSelectedLanguage } from '@frontend/translate';

type HolterProceduresListTableProps = {
    activeTab: ActiveOrArchived;
    holterProcesses: HolterRecorderItem[];
    reloadList: () => void;
};

type ModalProps = {
    processId?: string;
    recorderType?: RecorderType;
    requestId?: string;
};

export function HolterProceduresListTable(props: Readonly<HolterProceduresListTableProps>): JSX.Element {
    const { activeTab, holterProcesses, reloadList } = props;
    const [modalProps, setModalProps] = useState<ModalProps>({
        processId: undefined,
        recorderType: undefined,
        requestId: undefined,
    });
    const translate = getSelectedLanguage();

    return (
        <>
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translate.holter.recorder}</TableHeader>
                            <TableHeader>{translate.holter.patientName}</TableHeader>
                            <TableHeader>{translate.holter.configured}</TableHeader>
                            <TableHeader>{translate.holter.handedIn}</TableHeader>
                            <TableHeader>
                                {translate.holter.uploaded}
                                <Tooltip
                                    content="Zet de computer niet uit totdat de opname 100% is geupload"
                                    placement="top"
                                >
                                    <Icon
                                        color="primary"
                                        icon="information"
                                        sx={{ ml: 2 }}
                                    />
                                </Tooltip>
                            </TableHeader>
                            <TableHeader>{translate.holter.sent}</TableHeader>
                            <TableHeader>{translate.common.type}</TableHeader>
                            <TableHeader>{translate.common.status}</TableHeader>
                            <TableHeader>{translate.holter.diary}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {holterProcesses.map((holterProcess) => (
                            <TableRow key={holterProcess.processNumber}>
                                <TableCell>{holterProcess.holterSerialNumber}</TableCell>
                                <TableCell>
                                    <FullName {...holterProcess.patient} /> (
                                    <NullableStringBirthDate value={holterProcess.patient.birthDate} />)
                                </TableCell>
                                <TableCell>
                                    {holterProcess.configurationDatetime && (
                                        <NullableFormattedDatetime value={holterProcess.configurationDatetime} />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {holterProcess.downloadedDatetime && (
                                        <NullableFormattedDatetime value={holterProcess.downloadedDatetime} />
                                    )}
                                </TableCell>
                                <TableCell>{getProgressData(holterProcess.uploadProgress)}</TableCell>
                                <TableCell>
                                    {holterProcess.recordingSentDatetime && (
                                        <NullableFormattedDatetime value={holterProcess.recordingSentDatetime} />
                                    )}
                                </TableCell>
                                <TableCell>{getDisplayExaminationType(holterProcess)}</TableCell>
                                <TableCell>{getStatusLabel(holterProcess.status)}</TableCell>
                                <TableCell>{diaryData(holterProcess)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {(modalProps.requestId ?? modalProps.processId) && modalProps.recorderType && (
                <HolterReadAndUploadDairyModel
                    isDiaryUpload
                    onClose={async () => {
                        setModalProps((prevState) => ({
                            ...prevState,
                            processId: undefined,
                            recorderType: undefined,
                            requestId: undefined,
                        }));
                        reloadList();
                    }}
                    processId={modalProps.processId}
                    recorderType={modalProps.recorderType}
                    requestId={modalProps.requestId}
                />
            )}
        </>
    );

    function diaryData(holterRecorderItem: HolterRecorderItem): JSX.Element | undefined {
        if (holterRecorderItem.diaryType) {
            return getDiaryType(holterRecorderItem.diaryType);
        }

        if (holterRecorderItem.status === CustomControlStatus.assetNeeded) {
            const recorderType = holterRecorderItem.recorderType ?? RecorderType.fysiologic;

            return (
                <Button
                    color="primary"
                    onClick={async () => {
                        setModalProps({
                            processId: holterRecorderItem.processId,
                            recorderType: recorderType,
                            requestId: holterRecorderItem.requestId,
                        });
                    }}
                >
                    {translate.common.addTo}
                </Button>
            );
        }
    }

    function getDiaryType(diaryType: DiaryTypes): JSX.Element {
        switch (diaryType) {
            case DiaryTypes.none:
                return (
                    <Icon
                        color="primary"
                        icon="close"
                    />
                );
            case DiaryTypes.paperFax:
                return (
                    <>
                        <Icon
                            color="primary"
                            icon="closed"
                        />
                        {translate.holter.fax}
                    </>
                );
            case DiaryTypes.paperUpload:
                return (
                    <Icon
                        color="primary"
                        icon="closed"
                    />
                );
            default:
                throw new Error(`invalid diary type: ${diaryType}`);
        }
    }

    function getProgressData(progresss: number | null): string | undefined {
        if (!progresss) {
            return translate.common.inapplicable;
        }

        return `${progresss}%`;
    }

    function getStatusLabel(status: CustomControlStatus | null): string {
        if (activeTab === ActiveOrArchived.active) {
            switch (status) {
                case CustomControlStatus.withPatient:
                    return translate.StatusLabelEnum.withPatient;
                case CustomControlStatus.uploading:
                    return translate.upload.uploadToAnalyst;
                case CustomControlStatus.assetNeeded:
                    return translate.StatusLabelEnum.assetNeeded;
                case CustomControlStatus.analysis:
                    return translate.holter.withAnalyst;
                default:
                    break;
            }
        }

        return translate.procedure.status_completed;
    }

    function getDisplayExaminationType(holterRecorderItem: HolterRecorderItem): string | undefined {
        const duration = holterRecorderItem.opnameduur;
        switch (duration) {
            case HolterCardioExaminationDuration.oneDay:
            case OpnameDuur.eenDag:
                return translate.tcohProcedureTypeEnum.holter + ' (1d)';
            case HolterCardioExaminationDuration.twoDays:
            case OpnameDuur.tweeDagen:
                return translate.tcohProcedureTypeEnum.holter + ' (2d)';
            case HolterCardioExaminationDuration.threeDays:
            case OpnameDuur.drieDagen:
                return translate.tcohProcedureTypeEnum.holter + ' (3d)';
            case EventEcgCardioExaminationDuration.sevenDays:
            case OpnameDuur.zevenDagen:
                return translate.tcohProcedureTypeEnum.eventEcg + ' (7d)';
            case EventEcgCardioExaminationDuration.fourteenDays:
                return translate.tcohProcedureTypeEnum.eventEcg + ' (14d)';
            default:
                break;
        }
    }
}
